import {
    getModule,
    Module,
    Mutation,
    Action,
    VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';
import {
    PortfolioCompany,
    UserLogin,
    User,
    CompanyOverview,
    EventItem,
    CompanySie,
    CompanyDocument,
    UpdatedCompany,
} from '../models';
import * as api from '../api';
import version from '@/store/modules/version';

// tslint:disable:max-line-length

class CompanyData {
    overview: CompanyOverview | undefined;
    overviewLoaded: boolean = false;
    ranking: any | undefined;
    rankingLoaded: boolean = false;
    valuation: any | undefined;
    valuationLoaded: boolean = false;
    valuationOffset: number = 0;
    balance: any | undefined;
    balanceLoaded: boolean = false;
    profitloss: any | undefined;
    profitlossLoaded: boolean = false;
    cashflow: any | undefined;
    cashflowLoaded: boolean = false;
    sustainability: any | undefined;
    sustainabilityLoaded: boolean = false;
    sies: CompanySie[] | undefined;
    siesLoaded: boolean = false;
    documents: CompanyDocument[] | undefined;
    documentsLoaded: boolean = false;
    templatesLoaded: boolean = false;
}

@Module({
    namespaced: true,
    name: 'user',
    store,
    dynamic: true,
})
class UserModule extends VuexModule {
    public user: User | null = null;
    public version: number = 0;
    public companies: PortfolioCompany[] | null = null;
    public events: EventItem[] | null = null;
    public templates: CompanyDocument[] | null = null;
    public company: CompanyData = new CompanyData();
    public updates: UpdatedCompany[] | null = null;
    public loggingIn: boolean = false;
    public fetchingData: boolean = false;
    public fetchingMessage: string = '';
    public companyFilter: string = '';
    public selectedDocument: string | null = null;
    public selectedDocumentBlob: string | null = null;
    public activeSie: CompanySie | null = null;
    public selectedSie: string | null = null;
    public selectedSieBlob: string | null = null;
    public activeExternalUser: string | null = null;
    public templatesLoaded: boolean = false;

    @Action
    public async getCompany(orgNr: string) {
        if (this.activeExternalUser) {
            return api.getCompany(this.version, orgNr);
        }
        if (!this.companies) {
            return null;
        }
        for (const c of this.companies) {
            if (c.OrgNr === orgNr) {
                return c;
            }
        }
        return null;
    }

    @Action
    public getInternalCompany(orgNr: string) {
        if (this.activeExternalUser) {
            return null;
        }
        if (!this.companies) {
            return null;
        }
        for (const c of this.companies) {
            if (c.OrgNr === orgNr) {
                return c;
            }
        }
        return null;
    }

    @Action
    public async logout() {
        await api.logout();
        this.setUser(null);
        await this.getUser('Hämtar användare...');
    }

    @Action
    public async login(loginUser: UserLogin) {
        this.setLogginIn(true);
        await api.login(loginUser);
        await this.getUser('Hämtar bolagsdata');
        this.setLogginIn(false);
    }

    @Action
    public async reset() {
        this.setUser(null);
    }

    @Action
    public async refreshUserSilently() {
        await this.getUser('');
    }

    @Action
    public async checkUser() {
        if (version.version && this.user) {
            if (version.version.UserSeq > this.user.Seq) {
                this.setSelectedDocumentBlob(null);
                await this.refreshUserSilently();
            }
        }
    }

    @Action
    public async checkCompany(orgNr: string) {
        if (orgNr === '00') {
            return;
        }
        if (await api.updateCompany(orgNr)) {
            await this.addCompany(orgNr);
        }
    }

    @Action
    public async getUser(fetchMessage: string) {
        if (fetchMessage) {
            this.setFetchingMessage(fetchMessage);
            this.setFetchingData(true);
        }
        // TODO: Check versions before updating
        this.setUser(await api.getUser());
        if (this.user !== null) {
            this.setCompanies(await api.getCompanies(this.version));
            this.setEvents(await api.getEvents(this.version));
            this.setUpdates(await api.getUpdates()); // TODO: Extract?
            this.setTemplates(await api.getTemplates());
        } else {
            this.setCompanies(null);
            this.setEvents(null);
            this.setUpdates(null);
            this.setTemplates(null);
        }
        this.setFetchingData(false);
    }

    @Action
    public async addCompany(orgNr: string) {
        this.setFetchingMessage('Hämtar bolag');
        this.setFetchingData(true);
        await api.addCompany(orgNr);
        await this.getUser('Analyserar bolag');
        this.setFetchingData(false);
    }

    @Action
    public async getOverview(orgNr: string) {
        if (this.company.overviewLoaded) {
            return;
        }
        this.company.overviewLoaded = true;
        this.setOverview(await api.getOverview(this.version, orgNr));
    }

    @Action
    public async getRanking(orgNr: string) {
        if (this.company.rankingLoaded) {
            return;
        }
        this.company.rankingLoaded = true;
        if (this.activeSie) {
            this.setRanking(await api.getRankingSie(this.activeSie.AggVersion, orgNr));
        } else {
            this.setRanking(await api.getRanking(this.version, orgNr));
        }
    }

    @Action
    public async getBalance(orgNr: string) {
        if (this.company.balanceLoaded) {
            return;
        }
        this.company.balanceLoaded = true;
        if (this.activeSie) {
            this.setBalance(await api.getBalanceSie(this.activeSie.AggVersion, orgNr));
        } else {
            this.setBalance(await api.getBalance(this.version, orgNr));
        }
    }

    @Action
    public async getProfitLoss(orgNr: string) {
        if (this.company.profitlossLoaded) {
            return;
        }
        this.company.profitlossLoaded = true;
        if (this.activeSie) {
            this.setProfitLoss(await api.getProfitLossSie(this.activeSie.AggVersion, orgNr));
        } else {
            this.setProfitLoss(await api.getProfitLoss(this.version, orgNr));
        }
    }

    @Action
    public async getCashFlow(orgNr: string) {
        if (this.company.cashflowLoaded) {
            return;
        }
        this.company.cashflowLoaded = true;
        if (this.activeSie) {
            this.setCashFlow(await api.getCashFlowSie(this.activeSie.AggVersion, orgNr));
        } else {
            this.setCashFlow(await api.getCashFlow(this.version, orgNr));
        }
    }

    @Action
    public async getSustainability(orgNr: string) {
        if (this.company.sustainabilityLoaded) {
            return;
        }
        this.company.sustainabilityLoaded = true;
        if (this.activeSie) {
            this.setSustainability(await api.getSustainability(this.activeSie.AggVersion, orgNr));
        } else {
            this.setSustainability(await api.getSustainability(this.version, orgNr));
        }
    }

    @Action
    public async getValuation(args: any) {
        if (args.offset !== this.company.valuationOffset) {
            this.company.valuationLoaded = false;
        }
        if (this.company.valuationLoaded) {
            return;
        }
        this.company.valuationLoaded = true;
        this.company.valuationOffset = args.offset;
        if (this.activeSie) {
            this.setValuation(await api.getValuationSie(args.offset, this.activeSie.AggVersion, args.orgNr));
        } else {
            this.setValuation(await api.getValuation(args.offset, this.version, args.orgNr));
        }
    }

    @Action
    public async getSies(orgNr: string) {
        if (this.company.siesLoaded) {
            return;
        }
        this.company.siesLoaded = true;
        if (!this.company.sies) {
            this.setSies(await api.getSies(this.version, orgNr));
        }
    }

    @Action
    public async getDocuments(orgNr: string) {
        if (this.company.documentsLoaded) {
            return;
        }
        this.company.documentsLoaded = true;
        if (!this.company.documents) {
            this.setDocuments(await api.getDocuments(this.version, orgNr));
        }
    }

    @Action
    public async getTemplates() {
        if (this.templatesLoaded) {
            return;
        }
        // this.templatesLoaded = true; // TODO
        if (!this.templates) {
            this.setTemplates(await api.getTemplates());
        }
    }

    @Mutation
    public setActiveSie(sie: CompanySie | null) {
        this.company = new CompanyData();
        this.activeSie = sie;
    }

    @Mutation
    public setActiveExternalUser(user: string | null) {
        this.activeExternalUser = user;
    }

    @Mutation
    public resetCompany(user: string | null) {
        this.company = new CompanyData();
    }

    @Mutation
    public setFetchingData(fetchingData: boolean) {
        this.fetchingData = fetchingData;
    }

    @Mutation
    public setFetchingMessage(fetchingMessage: string) {
        this.fetchingMessage = fetchingMessage;
    }

    @Mutation
    setSelectedSie(sie: string | null) {
        this.selectedSie = sie;
    }

    @Mutation
    setSelectedSieBlob(sie: string | null) {
        this.selectedSieBlob = sie;
    }

    @Mutation
    setSelectedDocument(document: string | null) {
        this.selectedDocument = document;
    }

    @Mutation
    setSelectedDocumentBlob(documentBlob: string | null) {
        this.selectedDocumentBlob = documentBlob;
    }

    @Mutation
    setCompanyFilter(filter: string) {
        this.companyFilter = filter;
    }

    @Mutation
    private setUser(user: User | null) {
        this.user = user;
        if (user === null) {
            this.companies = null;
            this.events = null;
        }
        this.company = new CompanyData();
        if (user) {
            this.version = user.Version;
        } else {
            this.version = 0;
        }
    }

    @Mutation
    private setUpdates(updates: UpdatedCompany[] | null) {
        this.updates = updates;
    }

    @Mutation
    private setCompany(company: CompanyData) {
        this.company = company;
    }

    @Mutation
    private setOverview(overview: CompanyOverview | undefined) {
        this.company = Object.assign({}, this.company, { overview });
    }

    @Mutation
    private setRanking(ranking: any | undefined) {
        this.company = Object.assign({}, this.company, { ranking });
    }

    @Mutation
    private setBalance(balance: any | undefined) {
        this.company = Object.assign({}, this.company, { balance });
    }

    @Mutation
    private setProfitLoss(profitloss: any | undefined) {
        this.company = Object.assign({}, this.company, { profitloss });
    }

    @Mutation
    private setCashFlow(cashFlow: any | undefined) {
        this.company = Object.assign({}, this.company, { cashflow: cashFlow });
    }

    @Mutation
    private setSustainability(sustainability: any | undefined) {
        this.company = Object.assign({}, this.company, { sustainability });
    }

    @Mutation
    private setValuation(valuation: any | undefined) {
        this.company = Object.assign({}, this.company, { valuation });
    }

    @Mutation
    private setCompanies(companies: PortfolioCompany[] | null) {
        this.companies = companies;
    }

    @Mutation
    private setEvents(events: EventItem[] | null) {
        this.events = events;
    }

    @Mutation
    private setSies(sies: CompanySie[] | undefined) {
        this.company = Object.assign({}, this.company, { sies });
    }

    @Mutation
    private setDocuments(documents: CompanyDocument[] | undefined) {
        this.company = Object.assign({}, this.company, {
            documents,
            // documentsLoaded: documents !== undefined,
        });
    }

    @Mutation
    private setTemplates(templates: CompanyDocument[] | null) {
        this.templates = templates;
    }

    @Mutation
    private setLogginIn(loggingIn: boolean) {
        this.loggingIn = loggingIn;
    }
}

export default getModule(UserModule);
